import { PmiButton } from 'shell/components/shared/button';
import { Fragment, memo, ReactElement, useMemo, useRef, useState } from 'react';
import AccordionDocument from './accordion-document';
import { Form, Formik } from 'formik';
import { PracticeEditDocumentVM } from 'practice/view-models/practice-edit-document';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { noop } from 'shell/utils/noop';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import ErrorModal from 'shell/components/shared/modal-error';
import { DocumentiRichiestiVO } from 'shell/models/requested-document';
import { PracticeDraftStep } from 'practice/constants/practice-management-step';
import { TypeFile } from 'practice/components/detail/form/accordion-attachments/form';
import { AccordionAttachmentsVM } from 'practice/view-models/practice-detail';
import { Attachment } from 'shell/models/attachment';
import ModalWarning from 'shell/components/shared/modal-warning';
import { TypeRegister } from 'practice/constants/type-register';

interface StepDocumentProps {
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
  onBack: (step?: PracticeDraftStep) => void;
  onNext: (step?: PracticeDraftStep) => void;
  codOperatorePratica?: string;
  maxActiveStep: number;
  disabledAll?: boolean;
  iniziativa?: string;
  ForPracticeTemporary?: boolean;
}

const StepDocumentComponent = ({
  onBack,
  onNext,
  codeEnte,
  yearId,
  typeRegister,
  progressive,
  codOperatorePratica,
  maxActiveStep,
  disabledAll,
  ForPracticeTemporary,
}: StepDocumentProps): ReactElement => {
  const saveAddedFiles = useHttpClient<void>(
    `${__PRACTICE_API_URL__}/sezione-documenti/controllo/conferma-sezione-documenti`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const disabled = useMemo(() => {
    if (disabledAll) {
      return true;
    }

    // if (iniziativa === TypeInitiative.Anpr || iniziativa === TypeInitiative.ElixForm) {
    //   // ANPR o ELIXFORM
    //   return true;
    // }

    if (codOperatorePratica === 'BATCH') {
      return true;
    }

    return false;
  }, [codOperatorePratica, disabledAll]);

  const [openInitialWarningModal, setOpenInitialWarningModal] = useState<boolean>(false);
  const [openNextWarningModal, setOpenNextWarningModal] = useState<{
    open: boolean;
    data?: Partial<DocumentiRichiestiVO>;
  }>({ open: false });

  const documentNotLoaded = useRef<Attachment[]>();

  const [requiredDocuments] = useHttpFetch<
    HttpResponse<DocumentiRichiestiVO>,
    PracticeEditDocumentVM & DocumentiRichiestiVO
  >(`${__PRACTICE_API_URL__}/sezione-documenti/controllo/documenti-richiesti`, {
    onBootstrap: true,
    options: {
      method: 'POST',
      data: {
        praticaKeyVO: {
          codiceEnte: codeEnte,
          idAnnoPratica: yearId,
          tipoRegistro: typeRegister,
          progressivoPratica: progressive,
        },
      },
    },
    transformer: (response) => {
      if (
        response.data.payload?.documentRichiesti &&
        response.data.payload?.documentRichiesti?.length > 0
      ) {
        setOpenInitialWarningModal(true);
      }

      const files: Array<TypeFile> = (response.data.payload?.allegatoVOList || []).map((item) => {
        const file = new File(
          item.contenutoAllegato ? [item.contenutoAllegato] : [],
          item.nomeFile || ''
        );
        return {
          ...file,
          raw: (item.contenutoAllegato as string)
            ? `data:application/pdf;base64,${item.contenutoAllegato}`
            : undefined,
          name: item.nomeFile || '',
          idAllegato: item.key?.idAllegato,
        };
      });

      const descriptions: Array<string> = (response.data.payload?.allegatoVOList || []).map(
        (item) => item.descrizioneFile || ''
      );

      const attachments: AccordionAttachmentsVM = {
        files,
        isNotDocument: !(files?.length > 0),
        descriptions,
      };

      documentNotLoaded.current = response.data.payload.documentRichiesti || [];

      const addedFiles: Record<string, Array<Partial<TypeFile>>> = {};

      response.data.payload.allegatiRichiestiCaricati?.forEach((el) => {
        addedFiles[(el.descrizioneTipoAllegato as string) || ''] = [
          {
            ...el,
            idTipoAllegato: el.idTipoAllegato?.toString(),
            raw: (el.contenutoAllegato as string)
              ? `data:application/pdf;base64,${el.contenutoAllegato}`
              : undefined,
            name: el.nomeFile || '',
            idAllegato: el.key?.idAllegato,
          },
        ];
      });

      return {
        ...response.data.payload,
        documentRichiesti: (response.data.payload.documentRichiesti || []).concat(
          response.data.payload.allegatiRichiestiCaricati || []
        ),
        ...attachments,
        isNotDocument:
          attachments.isNotDocument && !Object.values(addedFiles || {}).some((el) => el.length > 0),
        addedFiles,
      };
    },
  });

  const requestedDocumentResponse =
    (requiredDocuments.data as DocumentiRichiestiVO & PracticeEditDocumentVM) || {};

  const initialValues: PracticeEditDocumentVM = useMemo(() => {
    if (requiredDocuments.isLoading && !requiredDocuments.data) {
      return {};
    }

    return {
      ...requiredDocuments.data,
      typeDocument: undefined,
    };
  }, [requiredDocuments.isLoading, requiredDocuments.data]);

  return (
    <Fragment>
      <Formik initialValues={initialValues} onSubmit={noop} enableReinitialize>
        {({ values }) => (
          <Form>
            {openInitialWarningModal ? (
              <ErrorModal
                open={openInitialWarningModal}
                onClose={() => {
                  setOpenInitialWarningModal(false);
                }}
                message={`${requestedDocumentResponse?.warning} \n\n ${
                  documentNotLoaded?.current
                    ?.map((item) => item?.descrizioneTipoAllegato)
                    .join('\n ') || ''
                }`}
              />
            ) : undefined}
            {openNextWarningModal?.open ? (
              <ModalWarning
                open={openNextWarningModal.open}
                onClose={() => {
                  setOpenNextWarningModal({ open: false });
                  onNext(PracticeDraftStep.AccertamentoMessi);
                }}
                nameButton="Vai Avanti"
                onAnnulment={() => {
                  setOpenNextWarningModal({ open: false });
                }}
                withAnnulment
                message={`${openNextWarningModal?.data?.warning} \n ${
                  openNextWarningModal?.data?.documentRichiesti
                    ?.map((item) => item?.descrizioneTipoAllegato)
                    .join('\n ') || ''
                } \n\n Si vuole proseguire ugualmente? `}
              />
            ) : undefined}
            <AccordionDocument
              codeEnte={codeEnte}
              yearId={yearId}
              typeRegister={typeRegister}
              progressive={progressive}
              disabled={disabled}
              maxActiveStep={maxActiveStep}
              requestedDocumentResponse={requestedDocumentResponse}
              consensoOspitante={requestedDocumentResponse?.flagConsensoOspitante === '1'}
            />
            <div className="pmi-pt-3 pmi-d-flex pmi-justify-content-center">
              <div className="pmi-w-25 pmi-w-lg-15">
                <PmiButton
                  variant="outlined"
                  fullWidth
                  color="cdmBrand"
                  onClick={() => onBack(PracticeDraftStep.Residence)}
                >
                  Indietro
                </PmiButton>
              </div>
              <div className="pmi-pl-2 pmi-w-25 pmi-w-lg-15">
                <PmiButton
                  variant="outlined"
                  fullWidth
                  color="cdmBrandThird"
                  onClick={() => {
                    (
                      saveAddedFiles({
                        data: {
                          praticaKeyVO: {
                            codiceEnte: codeEnte,
                            idAnnoPratica: yearId,
                            tipoRegistro: typeRegister,
                            progressivoPratica: progressive,
                          },
                        },
                      }) as ResponsePromise<HttpResponse<DocumentiRichiestiVO>>
                    ).then((response) => {
                      if (response?.data?.payload?.warning) {
                        setOpenNextWarningModal({ open: true, data: response.data.payload });
                        return;
                      }
                      onNext(
                        typeRegister !== TypeRegister.Emigration && !ForPracticeTemporary
                          ? PracticeDraftStep.AccertamentoMessi
                          : undefined
                      );
                    });
                  }}
                  disabled={
                    !(
                      ((values?.files || [])?.length > 0 &&
                        !(requestedDocumentResponse.flagConsensoOspitante === '1')) ||
                      values?.isNotDocument ||
                      (values.addedFiles &&
                        Object.values(values.addedFiles || {}).length > 0 &&
                        (documentNotLoaded.current || []).every(
                          (doc) =>
                            ((values.addedFiles || {})[doc.descrizioneTipoAllegato || ''] || [])
                              .length > 0
                        ) &&
                        requestedDocumentResponse.flagConsensoOspitante === '1') ||
                      (values.addedFiles &&
                        Object.values(values.addedFiles || {}).length > 0 &&
                        !(requestedDocumentResponse.flagConsensoOspitante === '1'))
                    )
                  }
                >
                  Avanti
                </PmiButton>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </Fragment>
  );
};

StepDocumentComponent.displayName = 'Practice Detail - Step Document';

const StepDocument = memo(StepDocumentComponent);

export default StepDocument;
